import postRobot from 'post-robot'
import { type AxiosStatic } from 'axios'
import { getTraceId } from '@/utils/traceId'
import { type ObjectSchema } from 'joi'
import type { AuthenticationMethod } from '@mc-alberta/types'
import { authenticate } from './authenticate'
import authenticateSchema from '../schemas/authenticate'
import authenticationMethodsLookupSchema from '../schemas/authenticationMethodsLookup'
import formatValidationError from '../utils/formatValidationError'
import { type AuthenticateError } from '@/types/AuthenticateError'
import { AuthenticateAction } from '@/types/enums/AuthenticateAction'
import Kpi from '../utils/kpi'
import { KpiOperation } from '@/types/enums/KpiOperation'
import type {
  AuthenticateRequest,
  AuthenticationMethodsLookupRequest,
  AuthenticationRequestParams
} from '../types/AuthenticateRequest'
import { getAuthenticateError } from '@/utils/getAuthenticateError'

const BASE_URL = import.meta.env.MASTERCARD_SRC_HOST

export class Sdk {
  /** Communicator iframe DOM element. */
  private iframe?: HTMLIFrameElement

  /** Correlation ID. Must be consistent between lookup and authenticate calls. */
  private srcCorrelationId = getTraceId()

  /** Trace ID, for KPI/events logging and site affinity. */
  private traceId = getTraceId()
  /**
   * Preloads the communicator iframe on SDK launch.
   */
  constructor() {
    this.getCommunicator()
  }

  /**
   * Launches the authentication UI.
   */
  public authenticate = async (
    params: AuthenticateRequest & {
      authenticationMethod: AuthenticationMethod
      windowRef?: Window
    }
  ) => {
    this.traceId = params.traceId || this.traceId
    this.srcCorrelationId = params.srcCorrelationId || this.srcCorrelationId

    const error = this.getValidationError(params, authenticateSchema)

    if (error) {
      return Promise.reject(error)
    }

    const windowRef = params.windowRef || window.open('', '', 'width=480,height=600')!

    try {
      return authenticate(
        {
          ...params,
          srcCorrelationId: this.srcCorrelationId,
          traceId: this.traceId,
          origin: BASE_URL!,
          windowRef
        },
        BASE_URL!
      )
    } catch (error) {
      const _error = error as AuthenticateError

      this.emitKpiFailure(params, {
        reason: _error.name,
        message: _error.message,
        errorDetails: {
          message: _error?.details?.message
        }
      })

      return Promise.reject(error)
    }
  }

  /**
   * Performs a lookup of all available authentication methods for the given card.
   */
  public authenticationMethodsLookup = async (params: AuthenticationMethodsLookupRequest) => {
    this.traceId = getTraceId(params.traceId)

    const error = this.getValidationError(params, authenticationMethodsLookupSchema)
    const communicator = await this.getCommunicator()

    if (error) {
      return Promise.reject(error)
    }

    if (!communicator) {
      return Promise.reject(getAuthenticateError('BLOCKED'))
    }

    const getAuthenticationMethods = async () => {
      try {
        const axios = this.getAxiosProxy(communicator)
        const { data } = (await axios({
          url: '/api/accountholder/authentications/lookup',
          data: {
            ...params,
            traceId: this.traceId,
            srcCorrelationId: this.srcCorrelationId
          }
        })) as {
          data: {
            authenticationMethods: AuthenticationMethod[]
          }
        }

        return data.authenticationMethods
      } catch (_) {
        return []
      }
    }

    return {
      traceId: this.traceId,
      srcCorrelationId: this.srcCorrelationId,
      srciTransactionId: params.srciTransactionId,
      authenticationMethods: await getAuthenticationMethods()
    }
  }

  /**
   * Returns the communicator Axios-like proxy instance.
   */
  getAxiosProxy = (communicator: Window): AxiosStatic => {
    return async function (message: object) {
      const { data } = await postRobot.send(communicator, AuthenticateAction.ApiRequest, message)

      return data
    } as AxiosStatic
  }

  /**
   * Instantiates a new communicator iframe window.
   */
  private getCommunicator = async (): Promise<Window | null> => {
    if (this.iframe?.contentWindow) {
      return this.iframe.contentWindow
    }

    this.iframe = document.createElement('iframe')
    this.iframe.style.display = 'none'

    document.body.appendChild(this.iframe)

    if (!this.iframe.contentWindow) {
      return null
    }

    this.iframe.contentWindow.location.href = `${BASE_URL}/auth/frame.html`

    const iframe = this.iframe

    return new Promise((resolve) => {
      postRobot.on(
        'Ready',
        {
          window: iframe.contentWindow
        },
        async () => resolve(iframe.contentWindow)
      )
    })
  }

  /**
   * Validates the given authentication parameters.
   *
   * @returns the Joi validation error object, or undefined if no errors present
   */
  private getValidationError = (params: AuthenticationRequestParams, schema: ObjectSchema) => {
    const { error } = schema.validate(params)

    if (!error) return

    this.emitKpiFailure(params, {
      reason: error.name,
      message: error.message,
      errorDetails: {
        message: error.details[0].message
      }
    })

    return formatValidationError(error)
  }

  /**
   * Sends a KPI failure event.
   */
  emitKpiFailure = async (
    params: AuthenticationRequestParams,
    errorData: {
      reason: string
      message?: string
      errorDetails: {
        message?: string
      }
    }
  ) => {
    const communicator = await this.getCommunicator()
    const proxy = this.getAxiosProxy(communicator!)
    const kpi = new Kpi(proxy, 'authsdk')

    kpi.setHeaders({
      srcCorrelationId: this.srcCorrelationId,
      srcClientId: params.srcClientId,
      serviceId: params.serviceId,
      traceId: this.traceId
    })

    kpi.track(
      {
        dpaData: {
          dpaId: params.authenticationContext?.srcDpaId
        },
        srciData: {
          srciId: params.srcClientId
        },
        cardData: {
          cardId: params.accountReference.srcDigitalCardId,
          cardVerificationData: {
            threeDsAuthVerificationType: params.authenticationMethod?.authenticationMethodType
          }
        },
        errorData
      },
      'FAILED',
      KpiOperation.AUTHENTICATE
    )
  }
}

const sdk = new Sdk()

window.AUTHSDK_MASTERCARD = sdk

export default sdk
